import {
  XBOT_CAPABILITIES_VERSION, XBOT_SUGGEST_VERSION, XbotCapabilities, XbotSuggestion, XbotSuggestRequest, XbotSuggestResponse,
} from "@dto/pyxbot-agent.dto";
import { errorBodyOf, refused, XBOT_CHAT_BASE } from "../agent/chatClient";
import type { SuggestOutcome } from "./flow";
import { ABORTED, askXbot, getJson, isObject, postJson, readJson } from "../xbotFetch";

// Two NG routes, both under the person's login: POST .../suggest and GET .../capabilities.
// Neither ever throws — a refusal comes back as a normal result instead.

export const XBOT_SUGGEST_URL = `${XBOT_CHAT_BASE}/suggest`;
export const XBOT_CAPABILITIES_URL = `${XBOT_CHAT_BASE}/capabilities`;

const NOTHING_TAKEN: XbotCapabilities = { schema_version: XBOT_CAPABILITIES_VERSION, bindings: false, suggest: false };

const SOURCES: ReadonlySet<string> = new Set(["last_answer", "thread", "ng"]);

function isRange(value: unknown): boolean {
  return isObject(value) && typeof value.start === "number" && typeof value.end === "number";
}

/** An offered object the editor can put into the text and bind: a name to insert, an item id, a known source. */
function usable(value: unknown): value is XbotSuggestion {
  if (!isObject(value) || value.type !== "object" || !isObject(value.target)) {
    return false;
  }
  return typeof value.label === "string" && value.label.length > 0
    && typeof value.insert_text === "string" && value.insert_text.length > 0
    && typeof value.target.item_id === "string" && value.target.item_id.length > 0
    && Array.isArray(value.distinguish) && value.distinguish.every((word) => typeof word === "string")
    && typeof value.source === "string" && SOURCES.has(value.source);
}

/** The offer in a reply, when the reply is one; objects the editor could not use are left out. */
function offerOf(payload: unknown): XbotSuggestResponse | null {
  if (!isObject(payload) || payload.schema_version !== XBOT_SUGGEST_VERSION) {
    return null;
  }
  if (typeof payload.draft_id !== "string" || typeof payload.request_id !== "number" || !isRange(payload.range)
    || !Array.isArray(payload.suggestions) || !isObject(payload.sources)) {
    return null;
  }
  return { ...(payload as unknown as XbotSuggestResponse), suggestions: payload.suggestions.filter(usable) };
}

const UNREADABLE = "The suggestions could not be read.";

/** Asks for the objects that match the editor's words; never throws. */
export async function sendXbotSuggest(body: XbotSuggestRequest, signal?: AbortSignal): Promise<SuggestOutcome> {
  const response = await askXbot(XBOT_SUGGEST_URL, postJson(body, signal));
  const payload = response && await readJson(response);
  if (signal?.aborted) {
    return ABORTED;
  }
  if (!response) {
    return refused(0, "unavailable", "Suggestions are not available right now.");
  }
  if (response.ok) {
    const offer = offerOf(payload);
    return offer ? { kind: "ok", response: offer } : refused(response.status, "bad_response", UNREADABLE);
  }
  const refusal = errorBodyOf(payload);
  if (refusal) {
    return { kind: "refused", status: response.status, error: refusal };
  }
  return response.status === 401 ? refused(401, "unauthorized", "Your session has ended.") : refused(response.status, "http_error", UNREADABLE);
}

// False from a 404 (older server), a down server, or any unrecognized answer — the editor then behaves as before.
export async function readXbotCapabilities(signal?: AbortSignal): Promise<XbotCapabilities> {
  const response = await askXbot(XBOT_CAPABILITIES_URL, getJson(signal));
  const payload = response?.ok ? await readJson(response) : null;
  if (isObject(payload) && payload.schema_version === XBOT_CAPABILITIES_VERSION
    && typeof payload.bindings === "boolean" && typeof payload.suggest === "boolean") {
    return { schema_version: XBOT_CAPABILITIES_VERSION, bindings: payload.bindings, suggest: payload.suggest };
  }
  return NOTHING_TAKEN;
}
